<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    data-id="our-process"
    class="flex flex-col"
  >
    <section
      v-if="introduction && (introduction.title || introduction.body || (introduction.cta && introduction.cta.length))"
      class="lg:w-2/3 flex flex-col items-center max-container mb-12"
    >
      <h2
        v-if="introduction.title"
        :id="introduction.title?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')"
        class="flex flex-col items-center text-center"
      >
        {{ introduction.title }}
      </h2>
      <div
        v-if="introduction.body"
        class="body-1 text-center lg:max-w-2/3"
        v-html="introduction.body"
      />
      <div
        v-if="introduction.cta && introduction.cta.length"
        class="flex flex-wrap justify-center gap-x-10 lg:gap-x-20"
      >
        <CtaLink
          v-for="(cta, idx) in introduction.cta"
          :key="idx"
          :href="cta.link_url"
          :external="cta.link_external"
          :text="cta.link_text"
        />
      </div>
    </section>
    <section class="w-full">
      <div class="max-container w-full border border-gray-100 bg-gray-50/50 p-4 bg-[radial-gradient(circle,var(--color-gray-100)_1px,transparent_1px)] bg-size-[20px_20px]">
        <div class="relative w-full md:w-1/2">
          <svg
            width="391"
            height="427"
            viewBox="0 0 391 427"
            fill="none"
            class="my-8 sm:mt-0 xl:mb-12 p-[10%] w-full h-auto text-gray-300"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <!-- Define the glow filter with a unique ID -->
              <filter
                id="glow"
                x="-50%"
                y="-50%"
                height="200%"
                width="200%"
              >
                <!-- Apply a Gaussian blur to the source graphic's alpha channel -->
                <feGaussianBlur
                  in="SourceGraphic"
                  stdDeviation="1"
                  result="blurred"
                />
                <!-- Merge the blurred effect behind the source graphic -->
                <feMerge>
                  <feMergeNode in="blurred" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <!-- Top arrows (combined) -->
            <path
              d="M201.699 77.2031L195.199 83.7031M195.199 83.7031L188.699 77.2031M195.199 83.7031V0.703125M195.199 0.703125L80.1992 0.70313M195.199 0.703125L310.199 0.70314"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- Top left (active) -->
            <path
              v-if="timer <= 0.1 && isPlaying"
              filter="url(#glow)"
              class="path-animate-r"
              d="M201.699 77.2031L195.199 83.7031M195.199 83.7031L188.699 77.2031M195.199 83.7031V0.703125L80.1992 0.70313"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />
            <!-- Top right (active) -->
            <path
              v-if="timer <= 0.1 && isPlaying"
              filter="url(#glow)"
              class="path-animate-r"
              d="M201.699 77.2031L195.199 83.7031M195.199 83.7031L188.699 77.2031M195.199 83.7031L195.199 0.703125L310.199 0.703135"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />

            <!-- Center arrows -->
            <path
              d="M201.695 242.705L195.195 249.205L188.695 242.705M195.197 249.203L195.197 210.703M195.197 210.703L188.697 217.203M195.197 210.703L201.697 217.203"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- active -->
            <path
              v-if="timer > 0.1 && timer <= 0.5 && isPlaying"
              filter="url(#glow)"
              class="path-animate-l-bounce"
              d="M201.695 242.705L195.195 249.205L188.695 242.705M195.197 249.203L195.197 210.703M195.197 210.703L188.697 217.203M195.197 210.703L201.697 217.203"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />

            <!-- Rectangle -->
            <rect
              x="113.899"
              y="93.3484"
              width="162.6"
              height="271.6"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-dasharray="3 5.5"
            />
            <!-- active -->
            <rect
              v-if="timer > 0.1 && timer <= 0.5 && isPlaying"
              filter="url(#glow)"
              class="rect-animate"
              x="113.899"
              y="93.3484"
              width="162.6"
              height="271.6"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-dasharray="3 5"
            />

            <!-- Curl right (top arrow) -->
            <path
              d="M389.697 169.705L383.197 176.205L376.697 169.705M383.199 176.203V148.703L285.199 149.203"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- active -->
            <path
              v-if="timer > 0.45 && timer <= 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-r-short"
              d="M389.697 169.705L383.197 176.205L376.697 169.705M383.199 176.203V148.703L285.199 149.203"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />
            <!-- Curl right (bottom arrow) -->
            <path
              d="M292.703 315.758L286.203 309.258M286.203 309.258L292.703 302.758M286.203 309.258L383.203 309.258V281.258"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- active -->
            <path
              v-if="timer > 0.45 && timer <= 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-r-short"
              d="M292.703 315.758L286.203 309.258M286.203 309.258L292.703 302.758M286.203 309.258L383.203 309.258V281.258"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />

            <!-- Curl left (top arrow) -->
            <path
              d="M0.699219 288.203L7.19922 281.703L13.6992 288.203M7.19742 281.705L7.19741 309.205L105.197 308.705"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- active -->
            <path
              v-if="timer > 0.45 && timer <= 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-r-short"
              d="M0.699219 288.203L7.19922 281.703L13.6992 288.203M7.19742 281.705L7.19741 309.205L105.197 308.705"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />
            <!-- Curl left (bottom arrow) -->
            <path
              d="M97.6992 142.148L104.199 148.648M104.199 148.648L97.6992 155.148M104.199 148.648H7.19922V176.648"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- active -->
            <path
              v-if="timer > 0.45 && timer <= 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-r-short"
              d="M97.6992 142.148L104.199 148.648M104.199 148.648L97.6992 155.148M104.199 148.648H7.19922V176.648"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />

            <!-- Bottom arrows -->
            <path
              d="M177.926 420.824L168.734 420.705L168.854 411.513M195.199 377.703C195.217 392.421 188.983 401.923 168.699 420.703"
              stroke="currentColor"
              stroke-opacity="0.5"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <path
              d="M212.473 420.703L221.664 420.584L221.545 411.418M195.199 377.703C195.182 392.38 201.416 401.855 221.699 420.582"
              stroke="currentColor"
              stroke-opacity="0.5"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <path
              d="M201.695 418.705L195.195 425.205L188.695 418.705M195.197 425.203L195.197 373.703"
              stroke="currentColor"
              :stroke-width="strokeWidth"
              stroke-linecap="round"
            />
            <!-- active -->
            <!-- left -->
            <path
              v-if="timer > 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-l-delay"
              d="M177.926 420.824L168.734 420.705L168.854 411.513M195.199 377.703C195.217 392.421 188.983 401.923 168.699 420.703"
              stroke="currentColor"
              stroke-opacity="0.5"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />
            <!-- right -->
            <path
              v-if="timer > 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-l-delay"
              d="M212.473 420.703L221.664 420.584L221.545 411.418M195.199 377.703C195.182 392.38 201.416 401.855 221.699 420.582"
              stroke="currentColor"
              stroke-opacity="0.5"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />
            <!-- middle -->
            <path
              v-if="timer > 0.75 && isPlaying"
              filter="url(#glow)"
              class="path-animate-l-quick"
              d="M201.695 418.705L195.195 425.205L188.695 418.705M195.197 425.203L195.197 373.703"
              stroke="currentColor"
              :stroke-width="blurStroke"
              stroke-linecap="round"
            />
          </svg>
          <button
            v-for="(_panel, index) in panels"
            :key="index"
            :class="[
              labelClasses,
              index === 0 ? '-translate-1/2 top-[9.5%] left-[13%]' : '',
              index === 1 ? '-translate-y-1/2 translate-x-1/2 top-[9.5%] right-[13%]' : '',
              index === 2 ? '-translate-y-1/2 -translate-x-1/2 top-[38%] left-[50%]' : '',
              index === 3 ? '-translate-y-1/2 -translate-x-1/2 top-[68%] left-1/2' : '',
              index === 4 ? 'translate-x-1/2 -translate-y-1/2 top-[52.9%] right-[11.5%]' : '',
              index === 5 ? '-translate-x-1/2 -translate-y-1/2 top-[52.9%] left-[11.5%]' : '',
              index === 6 ? '-translate-x-1/2 -translate-y-1/2 top-[99%] left-1/2' : '',
              activePanel === index ? activeClass : ''
            ]"
            @click="handlePanelClick(index)"
          >
            <div class="flex flex-row justify-center">
              <FaIcon
                :name="panels[index].icon"
                :icon-class="iconClass"
                icon-style="light"
              />
            </div>
            <p :class="titleClass">
              {{ panels[index].title }}
            </p>
          </button>
          <!-- Desktop Card - Hidden on mobile -->
          <div
            class="hidden md:block absolute top-1/2 -right-1/2 w-full my-4 px-8 -translate-y-1/2 translate-x-1/2"
          >
            <div class="p-8 bg-white border border-gray-100 relative">
              <div
                :class="progressIndicator"
                :style="{
                  width: `${timer * 100}%`
                }"
              />
              <FaIcon
                :name="panels[activePanel].icon"
                icon-style="light"
                icon-class="text-3xl xl:text-5xl mb-5 text-red-600"
              />
              <h3 class="mb-4!">
                {{ panels[activePanel].title }}
              </h3>
              <div
                class="body-2"
                v-html="panels[activePanel].content"
              />
              <div
                v-if="panels[activePanel].cta && panels[activePanel].cta.length"
              >
                <CtaLink
                  v-for="(cta, idx) in panels[activePanel].cta"
                  :key="idx"
                  :href="cta.link_url"
                  :external="cta.link_external"
                  :text="cta.link_text"
                  size="sm"
                />
              </div>
              <Controller
                v-model:is-playing="isPlaying"
                @pause="handlePause"
                @play="handlePlay"
                @next="handleNext"
                @previous="handlePrevious"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Mobile Modal -->
    <Teleport :to="`[data-uuid='${uuid}']`">
      <Transition name="modal-backdrop">
        <div
          v-if="isMobileModalOpen"
          class="fixed inset-0 bg-black/50 z-50 md:hidden"
          @click="closeMobileModal"
        />
      </Transition>
      <Transition name="modal-slide">
        <div
          v-if="isMobileModalOpen"
          class="content__sections fixed bottom-0 left-0 right-0 bg-white z-50 md:hidden max-h-[85vh] overflow-y-auto"
          @click.stop
        >
          <!-- Progress Indicator -->
          <div
            class="mx-0! max-w-full!"
            :class="progressIndicator"
            :style="{
              width: `${timer * 100}% !important`
            }"
          />
          <div class="p-8 relative">
            <!-- Close Button -->
            <button
              aria-label="Close modal"
              class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              @click="closeMobileModal"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <!-- Content -->
            <FaIcon
              :name="panels[activePanel].icon"
              icon-style="light"
              icon-class="text-4xl mb-5 text-red-600"
            />
            <h3 class="mb-4!">
              {{ panels[activePanel].title }}
            </h3>
            <div
              class="body-2"
              v-html="panels[activePanel].content"
            />
            <div
              v-if="panels[activePanel].cta && panels[activePanel].cta.length"
            >
              <CtaLink
                v-for="(cta, idx) in panels[activePanel].cta"
                :key="idx"
                :href="cta.link_url"
                :external="cta.link_external"
                :text="cta.link_text"
                size="sm"
              />
            </div>
            <Controller
              v-model:is-playing="isPlaying"
              @pause="handlePause"
              @play="handlePlay"
              @next="handleNext"
              @previous="handlePrevious"
            />
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, onMounted, onBeforeUnmount, ref } from 'vue';
import CtaLink from '../CtaLink/CtaLink.vue';
import Controller from './Controller.vue';

interface Panel {
  icon: string | null;
  title: string | null;
  content: string | null;
  cta: Array<Cta> | null;
}

interface Cta {
  link_text: string;
  link_url: string;
  link_external: boolean;
}

interface Introduction {
  title: string;
  body: string;
  cta: Array<Cta> | null;
}

interface OurProcess {
  introduction: Introduction;
  panels: Array<Panel>;
  uuid: string;
}

const props = defineProps<OurProcess>();

const activePanel = ref<number>(0);
const isPlaying = ref<boolean>(true);
const isMobileModalOpen = ref<boolean>(false);
let intervalId: number | null = null;

const labelClasses = [
  "absolute",
  "group",
  "flex",
  "flex-col",
  "gap-2",
  "xl:gap-3",
  "max-w-24",
  "md:max-w-28",
  "lg:max-w-28",
  "xl:max-w-36",
  "xl:min-w-36",
  "col-span-1",
  "pt-2 p-2",
  "md:pt-4",
  "lg:pt-4 lg:p-3",
  "xl:pt-5 xl:p-4",
  "border",
  "border-transparent",
  "hover:border-gray-100",
  "focus:border-gray-100",
  "hover:bg-white",
  "focus:bg-white",
].join(' ');

const activeClass = [
  "transition-all",
  "duration-150",
  "bg-white",
  "[&_p]:text-black",
  "border-gray-100!",
  "[&_i]:text-red-600!"
].join(' ');

const progressIndicator = [
  "absolute",
  "bg-red-600",
  "h-1",
  "w-full",
  "top-0",
  "left-0",
].join(' ');

const timer = ref<number>(0);

const startTimer = () => {
  if (intervalId) return; // Don't create multiple intervals
  intervalId = window.setInterval(() => {
    timer.value += 0.001;
    if (timer.value >= 1) {
      timer.value = 0;
      activePanel.value = (activePanel.value + 1) % props.panels.length;
    }
  }, 10);
};

const stopTimer = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

const handleResize = () => {
  // Close mobile modal if window is resized above md breakpoint
  if (window.innerWidth >= 768 && isMobileModalOpen.value) {
    isMobileModalOpen.value = false;
    // Resume auto-play when modal closes
    isPlaying.value = true;
    startTimer();
  }
};

onMounted(() => {
  if (isPlaying.value) {
    startTimer();
  }

  // Add resize listener
  window.addEventListener('resize', handleResize);

  onBeforeUnmount(() => {
    stopTimer();
    // Remove resize listener
    window.removeEventListener('resize', handleResize);
  });
});

const handlePause = () => {
  isPlaying.value = false;
  stopTimer();
};

const handlePlay = () => {
  isPlaying.value = true;
  startTimer();
};

const handleNext = () => {
  activePanel.value = (activePanel.value + 1) % props.panels.length;
  timer.value = 0;
};

const handlePrevious = () => {
  activePanel.value = (activePanel.value - 1 + props.panels.length) % props.panels.length;
  timer.value = 0;
};

const handlePanelClick = (index: number) => {
  activePanel.value = index;
  timer.value = 0;

  // Open modal on mobile (below md breakpoint)
  if (window.innerWidth < 768) {
    isMobileModalOpen.value = true;
  }
};

const closeMobileModal = () => {
  isMobileModalOpen.value = false;
  // Resume auto-play when modal closes
  isPlaying.value = true;
  startTimer();
};

const strokeWidth = ref<number>(1.4);
const blurStroke = ref<number>(0.5);

const iconClass = "text-gray-700 text-base sm:text-lg md:text-lg xl:text-4xl group-hover:text-red-600";
const titleClass = "text-center text-xs xl:text-base font-semibold text-gray-700 group-hover:text-red-600";

const rootElement = ref<HTMLElement | null>(null);

onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json'];
});
</script>

<style scoped>
.path-animate-r {
  color: var(--color-red-600);
  stroke-dasharray: 40,300;
  stroke-dashoffset: 40;
  animation: dash-r 1s ease infinite;
}

@keyframes dash-r {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 400;
    opacity: 0;
  }
}

.path-animate-r-short {
  color: var(--color-red-600);
  stroke-dasharray: 40,210;
  animation: dash-r-short 6s linear infinite;
}

@keyframes dash-r-short {
  0% {
    opacity: 0;
    stroke-dashoffset: -210;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 840;
    opacity: 0;
  }
}

.path-animate-l-bounce {
  color: var(--color-red-600);
  stroke-dasharray: 10,50;
  stroke-dashoffset: -100;
  animation: dash-bounce 4s linear infinite;
}

@keyframes dash-bounce {
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  25% {
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dashoffset: 100;
  }
  75% {
    stroke-dashoffset: 0;
  }
  90% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: -100;
    opacity: 0;
  }
}

.path-animate-l-quick {
  color: var(--color-red-600);
  stroke-dasharray: 20,150;
  animation: dash-quick 1450ms linear infinite;
}

@keyframes dash-quick {
  0% {
    opacity: 0;
    stroke-dashoffset: -100;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: 100;
    opacity: 0;
  }
}

.path-animate-l-delay {
  color: var(--color-red-600);
  stroke-dasharray: 10,150;
  animation: dash-delay 1.5s linear infinite;
}

@keyframes dash-delay {
  0% {
    opacity: 0;
    stroke-dashoffset: 160;
  }
  20% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    stroke-dashoffset: -60;
    opacity: 0;
  }
}

.rect-animate {
  color: var(--color-red-600);
  stroke-dasharray: 50,850;
  animation: rect 2s linear infinite;
}

@keyframes rect {
  0% {
    opacity: 0;
    stroke-dashoffset: 830;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    stroke-dashoffset: -50;
  }
}

/* Mobile Modal Transitions */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

.modal-slide-enter-active {
  transition: transform 0.3s ease-out;
}

.modal-slide-leave-active {
  transition: transform 0.25s ease-in;
}

.modal-slide-enter-from {
  transform: translateY(100%);
}

.modal-slide-leave-to {
  transform: translateY(100%);
}
</style>
